<template>
  <div v-loading="loading" class="detections-panel">
    <div v-if="!loading && catalog">
      <p v-if="hint" class="hint">{{ hint }}</p>

      <template v-for="group in visibleGroups" :key="group.driver">
        <h4 class="driver-title">{{ group.label }}</h4>
        <div class="detection-grid">
          <el-checkbox
            v-for="item in group.items"
            :key="item.key"
            v-model="item.active"
            :class="{ active: item.active }"
            :disabled="!editable"
          >
            {{ item.label }}
          </el-checkbox>
        </div>
      </template>

      <p v-if="isEmpty" class="empty">No active alerts.</p>

      <div v-if="editable" class="actions">
        <el-button type="primary" :loading="saving" @click="save">
          Save
        </el-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import { IDetectionCatalog } from '@/interfaces/ad_scans'

export default defineComponent({
  name: 'DetectionsPanel',
  props: {
    catalog: {
      type: Object as PropType<IDetectionCatalog | null>,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    }
  },
  emits: ['save'],
  setup(props, { emit }) {
    // Editable (admin) view shows every driver group to toggle; the read-only
    // (partner) view keeps only active alerts and hides groups with none.
    const visibleGroups = computed(() => {
      const groups = props.catalog ?? []
      if (props.editable) return groups

      return groups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => item.active)
        }))
        .filter((group) => group.items.length > 0)
    })

    const isEmpty = computed(
      () => !props.editable && visibleGroups.value.length === 0
    )

    const save = () => {
      if (!props.catalog) return

      const detections: Record<string, string[]> = {}
      props.catalog.forEach((group) => {
        detections[group.driver] = group.items
          .filter((item) => item.active)
          .map((item) => item.key)
      })

      emit('save', detections)
    }

    return { save, visibleGroups, isEmpty }
  }
})
</script>

<style scoped>
.hint {
  margin: 0 0 16px;
  color: #909399;
}

.empty {
  margin: 0;
  color: #909399;
}

.driver-title {
  margin: 20px 0 10px;
}

.driver-title:first-of-type {
  margin-top: 0;
}

.detection-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px 16px;
}

.actions {
  margin-top: 20px;
  text-align: right;
}

:deep(.el-checkbox__label) {
  color: #000 !important;
}

.active :deep(.el-checkbox__label) {
  color: #409eff !important;
  font-weight: 600;
}
</style>
